import { useState } from 'react';
import { MatchState, Player, MATCH_CONFIG, TeamState, Timeout } from '@/types';
import ServerSelectionModal from './ServerSelectionModal';
import FirstServerTeamSelection from './FirstServerTeamSelection';
import { exportSetData } from '@/lib/export';

interface SetWonBannerProps {
  matchState: MatchState;
  onStartNextSet: (firstServerId: string, firstServerTeamId?: string) => Promise<void>;
  onSelectFirstServerTeam?: (teamId: string) => void;
  completedTimeouts?: Timeout[];
}

export default function SetWonBanner({ matchState, onStartNextSet, onSelectFirstServerTeam, completedTimeouts = [] }: SetWonBannerProps) {
  const [phase, setPhase] = useState<'summary' | 'team-selection' | 'server-selection'>('summary');
  const [selectedTeamId, setSelectedTeamId] = useState<string | null>(null);
  const [isStarting, setIsStarting] = useState(false);
  const [isExporting, setIsExporting] = useState(false);

  if (!matchState.teamA || !matchState.teamB || !matchState.currentSet || !matchState.match) {
    return null;
  }

  const setId = matchState.currentSetNumber;
  const lastSet = matchState.sets[matchState.sets.length - 1];
  const setWinnerId = lastSet?.winnerId;

  if (!setWinnerId) return null;

  const winner = setWinnerId === matchState.teamA.id ? matchState.teamA : matchState.teamB;

  // Get the selected team for server selection
  const selectedTeam: TeamState = selectedTeamId === matchState.teamA.id ? matchState.teamA : matchState.teamB;

  const eligibleServers = { eligible: selectedTeam.players, ineligible: [] as { player: Player; remaining: number }[] };

  const handleStartNextSet = () => {
    // Show team selection first
    setPhase('team-selection');
  };

  const handleTeamSelect = (teamId: string) => {
    setSelectedTeamId(teamId);
    if (onSelectFirstServerTeam) {
      onSelectFirstServerTeam(teamId);
    }
    setPhase('server-selection');
  };

  const handleServerSelect = async (player: Player) => {
    setIsStarting(true);
    try {
      await onStartNextSet(player.id, selectedTeamId || undefined);
      setPhase('summary');
    } catch (err) {
      console.error('Failed to start next set:', err);
    } finally {
      setIsStarting(false);
    }
  };

  const handleExportSet = async () => {
    setIsExporting(true);
    try {
      await exportSetData(matchState, matchState.currentSetNumber);
    } catch (err) {
      console.error('Failed to export set:', err);
    } finally {
      setIsExporting(false);
    }
  };

  const handleBack = () => {
    if (phase === 'server-selection') {
      setPhase('team-selection');
    } else if (phase === 'team-selection') {
      setPhase('summary');
    }
  };

  return (
    <>
      {/* Team Selection Modal */}
      {phase === 'team-selection' && (
        <FirstServerTeamSelection
          isOpen={true}
          teamA={matchState.teamA}
          teamB={matchState.teamB}
          setNumber={setId + 1}
          onSelectTeam={handleTeamSelect}
          onCancel={handleBack}
        />
      )}

      {/* Server Selection Modal */}
      {phase === 'server-selection' && selectedTeamId && (
        <ServerSelectionModal
          team={selectedTeam}
          eligibleServers={{ eligible: eligibleServers.eligible, ineligible: eligibleServers.ineligible }}
          onSelect={handleServerSelect}
          reason="first-server"
          teamA={matchState.teamA}
          teamB={matchState.teamB}
          currentSetNumber={setId + 1}
          matchType={matchState.match.matchType}
          currentScoreA={0}
          currentScoreB={0}
          completedSets={matchState.sets}
          completedTimeouts={completedTimeouts}
          onBack={selectedTeamId ? handleBack : undefined}
        />
      )}

      {/* Summary Banner */}
      {phase === 'summary' && (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4">
          <div className="bg-surface border border-border rounded-2xl p-6 max-w-md w-full animate-fade-in">
            <h2 className="text-xl font-bold text-text-primary text-center mb-4">
              SET {setId} COMPLETE
            </h2>

            <div
              className="rounded-lg p-4 mb-4"
              style={{ backgroundColor: `${winner.color}15`, borderColor: winner.color, borderWidth: 2 }}
            >
              <p
                className="text-2xl font-bold text-center"
                style={{ color: winner.color }}
              >
                {winner.name.toUpperCase()} WIN SET {setId}
              </p>
            </div>

            {/* Final Score */}
            <div className="grid grid-cols-2 gap-4 mb-4">
              <div className="text-center">
                <p className="text-xs font-semibold uppercase mb-1" style={{ color: matchState.teamA?.color }}>
                  {matchState.teamA?.shortName}
                </p>
                <p className="font-mono-nums text-4xl font-bold text-text-primary">
                  {lastSet.scoreA}
                </p>
              </div>
              <div className="text-center">
                <p className="text-xs font-semibold uppercase mb-1" style={{ color: matchState.teamB?.color }}>
                  {matchState.teamB?.shortName}
                </p>
                <p className="font-mono-nums text-4xl font-bold text-text-primary">
                  {lastSet.scoreB}
                </p>
              </div>
            </div>

            {/* Sets Won */}
            <div className="flex items-center justify-center gap-4 mb-6">
              <div className="flex items-center gap-2">
                <p className="text-sm font-semibold" style={{ color: matchState.teamA?.color }}>
                  {matchState.teamA?.shortName}
                </p>
                <div className="flex gap-1">
                  {Array.from({ length: MATCH_CONFIG[matchState.match.matchType].maxSets }).map((_, i) => (
                    <span
                      key={i}
                      className={`w-3 h-3 rounded-full ${
                        i < matchState.teamA!.setsWon ? 'bg-team-a' : 'bg-border'
                      }`}
                    />
                  ))}
                </div>
              </div>
              <span className="text-text-muted">vs</span>
              <div className="flex items-center gap-2">
                <div className="flex gap-1">
                  {Array.from({ length: MATCH_CONFIG[matchState.match.matchType].maxSets }).map((_, i) => (
                    <span
                      key={i}
                      className={`w-3 h-3 rounded-full ${
                        i < matchState.teamB!.setsWon ? 'bg-team-b' : 'bg-border'
                      }`}
                    />
                  ))}
                </div>
                <p className="text-sm font-semibold" style={{ color: matchState.teamB?.color }}>
                  {matchState.teamB?.shortName}
                </p>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="space-y-3 mb-4">
              <button
                onClick={handleStartNextSet}
                disabled={isStarting}
                className="btn-primary bg-team-a text-white font-bold py-4"
              >
                {isStarting ? (
                  <span className="flex items-center justify-center gap-2">
                    <span className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    STARTING...
                  </span>
                ) : (
                  'START SET ' + (setId + 1)
                )}
              </button>

              <button
                onClick={handleExportSet}
                disabled={isExporting}
                className="btn-secondary w-full"
              >
                {isExporting ? (
                  <span className="flex items-center justify-center gap-2">
                    <span className="w-4 h-4 border-2 border-text-secondary border-t-transparent rounded-full animate-spin" />
                    EXPORTING...
                  </span>
                ) : (
                  'EXPORT SET DATA'
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
